import { useCallback, useEffect, useState } from "react";
import {
  ativarQuestao,
  lerQuestoes,
  salvarQuestao,
  type AlternativaDaQuestao,
  type QuestaoDaAvaliacao,
  type QuestoesDaAvaliacao,
} from "../lib/adminPos";
import Carregando from "./Carregando";

// Admin › Faculdade › Turmas › as QUESTÕES de uma avaliação (000228).
//
// ---------------------------------------------------------------------------
// Por que este editor existe, sendo que Victor pediu "criar avaliação"
// ---------------------------------------------------------------------------
// Criar a linha da disciplina não cria uma avaliação que FUNCIONE. Medido no
// corpo de `pos_iniciar_tentativa`: a prova é de **10 questões**, sorteadas
// entre as ativas, e abaixo disso o aluno que clicar recebe *"esta disciplina
// tem N questão(ões) cadastrada(s) e a prova é de 10 — fale com a secretaria"*.
//
// Sem esta tela, "criar avaliação" entregaria a metade que não serve: ela
// apareceria na grade, apareceria no boletim, e não poderia ser feita. E o
// estado não é hipotético — **28 disciplinas** estão exatamente assim hoje, sem
// que houvesse como consertar fora do SQL.
//
// ---------------------------------------------------------------------------
// A forma da questão é do BANCO, e ela foi MEDIDA
// ---------------------------------------------------------------------------
// Quatro alternativas (A a D) e UMA correta. A carga achou nove exceções em
// 2.245 e as NOMEOU em vez de engolir — oito com alternativa em branco e uma com
// duas corretas. O que entra por aqui não repete isso: quem recusa é
// `pos_admin_salvar_questao`, e a tela só não deixa o botão parecer disponível
// antes.
//
// ---------------------------------------------------------------------------
// O enunciado é um PARÁGRAFO, e isso é uma decisão
// ---------------------------------------------------------------------------
// `pos_questoes.enunciado` é jsonb de blocos (`paragrafo`, `lista`, `imagem`,
// `destaque`, `titulo`), e o acervo usa os quatro primeiros — porque veio do
// BBCode do Bubble. Este editor escreve só `paragrafo`: um editor de texto rico
// completo é outra tela, e entregá-lo pela metade produziria questão com
// marcação quebrada no meio da prova. Questão que precise de imagem ou lista
// continua vindo pela carga, e a tela não finge que faz.

const LETRAS = ["A", "B", "C", "D"] as const;

function alternativasVazias(): AlternativaDaQuestao[] {
  return LETRAS.map((letra) => ({ letra, conteudo: "", correta: letra === "A" }));
}

export default function AdminPosQuestoes({
  disciplina,
  aoMudar,
}: {
  disciplina: string;
  /** A grade mostra quantas questões a avaliação tem: ela precisa saber. */
  aoMudar?: () => void | Promise<void>;
}) {
  const [dados, setDados] = useState<QuestoesDaAvaliacao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [editando, setEditando] = useState<string | "nova" | null>(null);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    setDados(await lerQuestoes(disciplina));
    setCarregando(false);
  }, [disciplina]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  async function depoisDeSalvar() {
    setEditando(null);
    await recarregar();
    await aoMudar?.();
  }

  if (carregando && !dados) return <Carregando />;
  if (!dados?.ok) {
    return (
      <p className="rounded-cf-lg bg-erro-fundo p-3 text-sm text-erro-tinta">
        Não conseguimos carregar as questões desta avaliação.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* O estado da prova vem do BANCO (`abre_prova`), não de uma conta feita
          aqui: duas definições de "a prova abre?" divergiriam no dia em que o
          mínimo mudasse. */}
      <div
        className={`rounded-cf-lg p-3 text-sm ${
          dados.abre_prova ? "bg-aviso-fundo text-aviso-tinta" : "bg-alerta-fundo text-alerta-tinta"
        }`}
      >
        {dados.abre_prova ? (
          <>
            <strong>A prova abre.</strong> {dados.ativas} questões ativas — a prova sorteia{" "}
            {dados.minimo}.
          </>
        ) : (
          <>
            <strong>A prova ainda não abre.</strong> São {dados.ativas} questão
            {dados.ativas === 1 ? "" : "ões"} ativa{dados.ativas === 1 ? "" : "s"} de {dados.minimo}{" "}
            — faltam {dados.minimo - dados.ativas}. Enquanto estiver assim, o aluno que tentar
            fazê-la recebe uma recusa.
          </>
        )}
      </div>

      <ul className="flex flex-col gap-2">
        {dados.questoes.map((q, i) =>
          editando === q.questao_id ? (
            <li key={q.questao_id}>
              <FormularioDaQuestao
                disciplina={disciplina}
                questao={q}
                aoFechar={() => setEditando(null)}
                aoSalvar={depoisDeSalvar}
              />
            </li>
          ) : (
            <LinhaDaQuestao
              key={q.questao_id}
              questao={q}
              numero={i + 1}
              aoEditar={() => setEditando(q.questao_id)}
              aoMudar={depoisDeSalvar}
            />
          ),
        )}
        {dados.questoes.length === 0 && (
          <li className="text-sm text-cinza">Esta avaliação ainda não tem nenhuma questão.</li>
        )}
      </ul>

      {editando === "nova" ? (
        <FormularioDaQuestao
          disciplina={disciplina}
          aoFechar={() => setEditando(null)}
          aoSalvar={depoisDeSalvar}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditando("nova")}
          className="pill-cta min-h-11 self-start px-5"
        >
          Nova questão
        </button>
      )}
    </div>
  );
}

function LinhaDaQuestao({
  questao,
  numero,
  aoEditar,
  aoMudar,
}: {
  questao: QuestaoDaAvaliacao;
  numero: number;
  aoEditar: () => void;
  aoMudar: () => Promise<void>;
}) {
  const [mexendo, setMexendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function alternar() {
    setMexendo(true);
    setErro(null);
    const r = await ativarQuestao(questao.questao_id, !questao.ativa);
    setMexendo(false);
    if (!r.ok) {
      // Erro de UMA questão fica NA LINHA: trocar a lista inteira por uma
      // mensagem apagaria da tela as outras, que estão certas.
      setErro(r.detalhe ?? "Não deu para mudar.");
      return;
    }
    await aoMudar();
  }

  return (
    <li
      className={`surface rounded-cf-lg p-3 ${questao.ativa ? "" : "border border-dashed border-borda"}`}
      data-questao={questao.questao_id}
    >
      <div className="flex flex-wrap items-start gap-3">
        <span className="w-6 text-center text-xs font-bold text-cinza tabular-nums">{numero}</span>
        <div className="min-w-0 flex-1">
          <p className={`text-sm ${questao.ativa ? "text-tinta" : "text-cinza line-through"}`}>
            {questao.texto}
          </p>
          <ul className="mt-1.5 flex flex-col gap-0.5">
            {questao.alternativas.map((a) => (
              <li
                key={a.letra}
                className={`text-xs ${a.correta ? "font-bold text-aviso-tinta" : "text-cinza"}`}
              >
                {a.letra}) {a.conteudo}
                {a.correta && " ✓"}
              </li>
            ))}
          </ul>
          {/* Editar questão JÁ RESPONDIDA muda a prova de quem a fez: a tentativa
              guarda a resposta, não o enunciado. A edição continua permitida —
              corrigir um erro de digitação é o caso comum, e proibi-la obrigaria
              a desativar e recadastrar, o que quebraria o histórico de verdade —,
              mas o operador precisa saber. */}
          {questao.usada_em_tentativas && (
            <p className="mt-1.5 text-xs text-alerta-tinta">
              Esta questão já foi respondida por alunos. Editar o enunciado muda o que eles viram.
            </p>
          )}
          {erro && <p className="mt-1.5 text-xs text-erro-tinta">{erro}</p>}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={aoEditar}
            className="h-9 rounded-cf border border-borda px-3 text-xs font-semibold text-tinta transition hover:border-laranja"
          >
            Editar
          </button>
          <button
            type="button"
            onClick={alternar}
            disabled={mexendo}
            className="h-9 rounded-cf border border-borda px-3 text-xs font-semibold text-tinta transition hover:border-laranja"
          >
            {questao.ativa ? "Desativar" : "Ativar"}
          </button>
        </div>
      </div>
    </li>
  );
}

function FormularioDaQuestao({
  disciplina,
  questao,
  aoFechar,
  aoSalvar,
}: {
  disciplina: string;
  questao?: QuestaoDaAvaliacao;
  aoFechar: () => void;
  aoSalvar: () => Promise<void>;
}) {
  const [texto, setTexto] = useState(questao?.texto ?? "");
  const [alternativas, setAlternativas] = useState<AlternativaDaQuestao[]>(
    questao?.alternativas?.length === 4 ? questao.alternativas : alternativasVazias(),
  );
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function mudarConteudo(letra: string, conteudo: string) {
    setAlternativas(alternativas.map((a) => (a.letra === letra ? { ...a, conteudo } : a)));
    setErro(null);
  }

  // UMA correta: marcar B desmarca A. O banco recusa duas, e deixar a tela
  // permitir marcar duas seria convidar a um erro que só aparece ao salvar.
  function marcarCorreta(letra: string) {
    setAlternativas(alternativas.map((a) => ({ ...a, correta: a.letra === letra })));
    setErro(null);
  }

  const completa = texto.trim() !== "" && alternativas.every((a) => a.conteudo.trim() !== "");

  async function salvar() {
    setSalvando(true);
    setErro(null);
    const r = await salvarQuestao(disciplina, texto, alternativas, questao?.questao_id);
    setSalvando(false);
    if (!r.ok) {
      setErro(r.detalhe ?? "Não deu para salvar a questão.");
      return;
    }
    await aoSalvar();
  }

  return (
    <div className="surface rounded-cf-lg border border-acento/40 p-4">
      <h4 className="text-xs font-bold tracking-widest text-cinza uppercase">
        {questao ? "Editar questão" : "Nova questão"}
      </h4>

      <label className="mt-3 block text-xs font-bold tracking-widest text-cinza uppercase" htmlFor="enunciado">
        Enunciado
      </label>
      <textarea
        id="enunciado"
        value={texto}
        onChange={(e) => {
          setTexto(e.target.value);
          setErro(null);
        }}
        rows={4}
        className="mt-1.5 w-full rounded-cf border border-borda bg-superficie p-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
        placeholder="A pergunta, como o aluno vai lê-la."
      />

      <p className="mt-4 text-xs font-bold tracking-widest text-cinza uppercase">
        Alternativas — marque a correta
      </p>
      <div className="mt-1.5 flex flex-col gap-2">
        {alternativas.map((a) => (
          <div key={a.letra} className="flex items-center gap-2">
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name="correta"
                checked={a.correta}
                onChange={() => marcarCorreta(a.letra)}
                className="accent-acento"
                aria-label={`A alternativa ${a.letra} é a correta`}
              />
              <span className="w-4 text-sm font-bold text-tinta">{a.letra}</span>
            </label>
            <input
              value={a.conteudo}
              onChange={(e) => mudarConteudo(a.letra, e.target.value)}
              aria-label={`Texto da alternativa ${a.letra}`}
              className="h-11 flex-1 rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
            />
          </div>
        ))}
      </div>

      {erro && <p className="mt-3 rounded-cf bg-erro-fundo p-2.5 text-sm text-erro-tinta">{erro}</p>}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={salvar}
          disabled={!completa || salvando}
          className={completa && !salvando ? "pill-cta min-h-11 px-5" : "pill-desligada min-h-11 px-5"}
        >
          {salvando ? "Salvando…" : "Salvar questão"}
        </button>
        <button type="button" onClick={aoFechar} className="text-sm font-semibold text-link underline">
          Cancelar
        </button>
        {!completa && (
          <span className="text-xs text-cinza">
            O enunciado e as quatro alternativas precisam estar preenchidos.
          </span>
        )}
      </div>
    </div>
  );
}
